import { getShippoInfo } from '../../api/orders/shippo';
import {
  shopifyGetQuery,
  shopifyPostPutQuery,
  shopifyGetMultiQuery,
  updateOrderAddress,
  shopifyRefundAmount,
} from '../../api/orders/shopify';

import { setLoading } from '../index';
import {
  clearOrders,
  selectOrder,
  setOrders,
  setRefundsOnShopifyOrder,
  setShopifyOrderShippingAddress,
  setShowConfirmationPage,
} from './OrdersSearchActions';
import { getMappedValueFromOrderOrItem, UnsupportedCarrier } from '../../util';
import { setShippoTrackingInfo } from '../searchActions';
import { showError, showErrorMessage, showExpressError } from '../../util/errorPopupUtil';
import {
  getSelectedOrder,
  getSelectedShop,
  getShopifyShopNamesForSelectedTicket,
  getShopNameFromOrdersByOrderId,
  getSelectedOrderId,
  getOrderingSystem,
  getSelectedLineItems,
  getRefundAmount,
} from '../../selectors/tools';
import { returnOrder } from '../../analytics/orderEventAnalytics';
import get from 'lodash/get';
import { GeoTypes, getGeoDataOptionsCodeByName } from './../../constants/geodata';
import { getShippoCarrierFromShopifyCarrierWithoutError } from '../../util/decisionTreeUtil';
import {
  extractSelectedTicketFromState,
  extractSelectedTicketIdFromState,
  getCancelAndRefundItems,
  getRefundOnlyItems,
  getSelectedCouponCodeType,
  getSimplrAccountIdOfSelectedTicket,
  getActionReasons,
} from 'extractFromState';
import { handleError } from 'services/ErrorService/ErrorService';
import { getExpertId } from '../../util/parseSelectors';
import {
  trackDecisionTreeItemPartiallyRefunded,
  trackDecisionTreeItemReplacedWithCoupon,
} from '../../analytics/searchEventAnalytics';
import HttpStatus from 'http-status-codes';

function shopifyGetMulti(api, queries) {
  return async (dispatch) => {
    dispatch(clearOrders());

    try {
      const orders = await shopifyGetMultiQuery({ api, queries });

      return dispatch(setOrders(orders));
    } catch (err) {
      if (err.status === HttpStatus.UNPROCESSABLE_ENTITY) {
        let customMessage = err.message.split(/\n/)?.[1] + '. Please search by email or order id';

        return handleError({ customMessage, showPopup: true });
      }

      handleError({ error: err, customMessage: 'Error getting customer orders' });

      if (err.status === HttpStatus.NOT_FOUND) {
        const error = 'Could not retrieve relevant information from Shopify.';
        dispatch(clearOrders());

        return showErrorMessage(error);
      }

      return showExpressError(err);
    } finally {
      dispatch(setLoading(false, 'searchPage'));
    }
  };
}

export const shopifyGetOrder = (query, shopifyNames = null) => {
  return async (dispatch, getState) => {
    const shopNames = shopifyNames ? shopifyNames : getShopifyShopNamesForSelectedTicket(getState());

    const queries = shopNames.map((shopNameItem) => {
      return Object.assign({}, query, {
        shop: shopNameItem,
      });
    });

    return dispatch(shopifyGetMulti('/getOrder', queries));
  };
};

export const shopifyGetCustomerOrders = (query, shopifyNames = null) => {
  return async (dispatch, getState) => {
    const shopNames = shopifyNames ? shopifyNames : getShopifyShopNamesForSelectedTicket(getState());

    if (query.email) {
      query['query'] = `email:"${query.email}"`;
      delete query['email'];
    }

    const queries = shopNames.map((shopNameItem) => {
      return Object.assign({}, query, {
        shop: shopNameItem,
      });
    });

    return dispatch(shopifyGetMulti('/getCustomerOrders', queries));
  };
};

const updateSelectOrder = (order, shopName) => {
  return async (dispatch) => {
    return dispatch(
      selectOrder({
        ...order,
        shopName,
      })
    );
  };
};

export const shopifyGetAndSelectOrder = (orderId) => {
  return async (dispatch, getState) => {
    const state = getState();
    const shopName = getShopNameFromOrdersByOrderId(state, orderId);
    const response = await shopifyGetQuery('/getOrderById', { id: orderId, shop: shopName });

    return dispatch(updateSelectOrder(response, shopName));
  };
};

export const shopifyUpdateOrderState = (orderId, shopName) => {
  return async (dispatch) => {
    const response = await shopifyGetQuery('/getOrderById', { id: orderId, shop: shopName });

    return dispatch(updateSelectOrder(response, shopName));
  };
};

export const shopifyUpdateOrder = (shopName, orderId, orderChanges) => {
  return async (dispatch) => {
    const updateOrderQuery = {
      shop: shopName,
    };

    try {
      await shopifyPostPutQuery('put', `/order/${orderId}`, orderChanges, updateOrderQuery);

      return dispatch(shopifyGetAndSelectOrder(orderId));
    } catch (err) {
      if (err.status === 400) {
        let error = get(err, 'response.text', null);

        // Specific error case for ZIP code
        if (typeof error === 'string' && error.includes('Zip is not valid')) {
          const state = get(orderChanges, 'shipping_address.province_code', null);
          error = state
            ? `ZIP is not valid for ${getGeoDataOptionsCodeByName(GeoTypes.PROVINCE_LIST, state)} and United States.`
            : `ZIP is not valid for United States.`;
        }

        showErrorMessage(error);
        throw err;
      }

      showExpressError(err);
      throw err;
    }
  };
};

/**
 * @typedef {object} ShopifyReplaceOrderPayload
 * @property {string} shopName
 * @property {string} simplrAccountId
 * @property {object[]} items
 * @property {number} items[].variant_id
 * @property {number} items[].quantity
 */

/**
 * Create new order and track replacement event
 * @param {string} orderId
 * @param {ShopifyReplaceOrderPayload} params
 * @returns {Function}
 */
export const shopifyReplaceOrder = (orderId, params) => async () => {
  return shopifyPostPutQuery('post', `/order/${orderId}/replace`, params);
};

export const shopifyReplaceOrderWithCoupon = () => async (dispatch, getState) => {
  const state = getState();
  const selectedOrder = getSelectedOrder(state);
  const orderingSystem = getOrderingSystem(state);
  const orderId = getMappedValueFromOrderOrItem(selectedOrder, 'orderId', orderingSystem);

  const selectedCouponCodeType = getSelectedCouponCodeType(state);
  const selectedItems = getSelectedLineItems(state);
  const selectedTicket = extractSelectedTicketFromState(state);
  const simplrAccountId = getSimplrAccountIdOfSelectedTicket(state);
  const selectedShop = getSelectedShop(state);

  if (!selectedCouponCodeType) {
    throw new Error('A coupon code must be selected to issue a replacement.');
  }

  const couponCodeTypeId = selectedCouponCodeType.id;

  const items = selectedItems
    .map((orderItem) => ({
      variant_id: orderItem.variant_id,
      quantity: orderItem.quantity,
      sku: orderItem.sku,
      id: orderItem.id,
    }))
    .filter(({ quantity }) => quantity > 0);

  const params = {
    simplrAccountId,
    couponCodeTypeId,
    items,
    conversationId: selectedTicket.ticketId,
    shopName: selectedShop,
    expertId: getExpertId(),
    couponCodeTypeName: selectedCouponCodeType.name,
    platformTicketId: selectedTicket.platformTicketId,
  };

  await shopifyPostPutQuery('post', `/order/${orderId}/replaceWithCoupon`, params);
  trackDecisionTreeItemReplacedWithCoupon();
};

/**
 * Track return event
 * @returns {function(): Promise<void>}
 * @param conversationObjectId
 */
export const shopifyReturnOrder = (conversationObjectId) => async () => {
  return returnOrder(conversationObjectId);
};

/**
 * @typedef {object} ShopifyAddOrderNotePayload
 * @property {string} shopName
 * @property {string} simplrAccountId
 * @property {string} note
 */

/**
 * Create note for shopify order
 * @param {string} orderId
 * @param {ShopifyAddOrderNotePayload} params
 * @returns {Function}
 */
export const shopifyAddOrderNote = (orderId, params) => async () => {
  return shopifyPostPutQuery('post', `/order/${orderId}/note`, params);
};

export const shopifyDecisionTreeSelectOrder = (order) => async (dispatch, getState) => {
  // load shippo
  if (order.fulfillments) {
    const shippoInfoList = await Promise.all(
      order.fulfillments.map(async (fulfillment) => {
        if (fulfillment.tracking_number) {
          try {
            // shippo will fail if tracking number has a trailing whitespace
            const tracking_number = fulfillment.tracking_number && fulfillment.tracking_number.trim();
            const tracking_company = fulfillment.tracking_company;
            const shipment_status = fulfillment.shipment_status;
            if (!shipment_status) {
              const shippoCarrierCode = getShippoCarrierFromShopifyCarrierWithoutError(tracking_company);
              if (shippoCarrierCode) {
                return await getShippoInfo(shippoCarrierCode, tracking_number);
              }
            }
          } catch (err) {
            // if unsupported carrier, return null
            if (err instanceof UnsupportedCarrier) {
              // TODO Jack: Why are we returning null as a string?
              return 'null';
            }
            handleError({ error: err, showPopup: true });
          }
        }

        return null;
      })
    );
    // iterate through our lists of shippo and order shipments
    // TODO refactor this to dispatch a single action with needed update
    for (let i = 0; i < order.fulfillments.length; i++) {
      const shippoInfo = shippoInfoList[i];
      const { id } = order.fulfillments[i];
      // check there was a shippo here
      if (shippoInfo) {
        dispatch(setShippoTrackingInfo(id, shippoInfo));
      }
    }
  }

  return order;
};

/**
 * Shopify resend email
 * @param {object} options
 * @param {string} options.customerId
 * @param {array} options.conversationId
 * @param {array} options.addNotes
 * @return {Function}
 */
export const shopifySendAccountCreationEmail = (options) => async (dispatch, getState) => {
  const { customerId, conversationId, addNotes } = options;
  const state = getState();
  const shopName = getSelectedShop(state);

  const payload = {
    customerId,
    shopName,
  };

  if (conversationId && addNotes) {
    payload.conversationId = conversationId;
    payload.addNotes = addNotes;
  }

  return shopifyPostPutQuery('post', '/sendInviteEmail', payload);
};

export const shopifyGetOrderShippingRefundAmount = ({ orderId, shopName }) => {
  return async (dispatch) => {
    const response = await shopifyGetQuery('/order/shipping/refundAmount', { orderId, shopName });

    return get(response, 'shippingRefundAmount');
  };
};

export const shopifyRefundOrderShippingAmount = (options) => async (dispatch, getState) => {
  const { orderId, comment, conversationId, addNotes, shopName } = options;
  const payload = {
    orderId,
    comment,
    shopName,
  };
  if (conversationId && addNotes) {
    payload.conversationId = conversationId;
    payload.addNotes = addNotes;
  }

  return shopifyPostPutQuery('post', '/refundOrderShipping', payload);
};

export const createLastMileRefund = (simplrAccountId, conversationId, reason) => async (dispatch, getState) => {
  try {
    dispatch(setLoading(true, 'reasonContainer'));
    const state = getState();
    const selectedOrder = getSelectedOrder(state);
    const refundOnlyItems = getRefundOnlyItems(state);
    const cancelAndRefundItems = getCancelAndRefundItems(state);

    const orderNumber = selectedOrder?.order_number;
    const createdAt = selectedOrder?.created_at;

    const validItems = [...refundOnlyItems, ...cancelAndRefundItems];

    const extraLastMileInfo = {
      reason,
      orderNumber,
      createdAt,
      items: {},
    };

    validItems.forEach((validItem) => {
      const fulfillmentData = selectedOrder.fulfillments.find((fulfillment) => {
        const lineItems = fulfillment.line_items || fulfillment.shipmentItems || [];

        return lineItems.some((item) => item.id === validItem.id || String(item.lineItemKey) === String(validItem.id));
      });

      const isFulfilled = !!fulfillmentData;
      const carrier = fulfillmentData?.tracking_company ?? '';
      const trackingLinks = fulfillmentData?.tracking_urls ?? [];
      const sku = validItem?.sku ?? '';
      const itemData = {
        trackingLinks,
        sku,
        carrier,
        isFulfilled,
      };
      extraLastMileInfo.items[validItem.id] = itemData;
    });

    const payload = {
      simplrAccountId,
      conversationId,
      extraLastMileInfo,
    };

    await shopifyPostPutQuery('post', '/createLastMileRefund', payload);
  } catch (error) {
    showError(error);
  } finally {
    dispatch(setLoading(false, 'reasonContainer'));
  }
};

export const shopifyChangeOrderAddress = (formData) => async (dispatch, getState) => {
  try {
    dispatch(setLoading(true, 'itemTransactionContainer'));

    const state = getState();
    const simplrAccountId = getSimplrAccountIdOfSelectedTicket(state);
    const orderId = getSelectedOrderId(state);
    const shopName = getSelectedShop(state);
    const conversationId = extractSelectedTicketIdFromState(state);

    const params = {
      simplrAccountId,
      conversationId,
      orderId,
      shopName,
      shippingAddress: formData,
    };

    const {
      order: { shipping_address },
    } = await updateOrderAddress(params);
    dispatch(setShopifyOrderShippingAddress(shipping_address));
    dispatch(setShowConfirmationPage());
  } catch (error) {
    handleError({
      error,
      customMessage: error.response?.text,
      showPopup: true,
    });
  } finally {
    dispatch(setLoading(false, 'itemTransactionContainer'));
  }
};

export const shopifyPriceMatchPartialRefund = () => async (dispatch, getState) => {
  const state = getState();

  const selectedOrder = getSelectedOrder(state);
  const orderingSystem = getOrderingSystem(state);
  const shopName = getSelectedShop(state);
  const orderId = getMappedValueFromOrderOrItem(selectedOrder, 'orderId', orderingSystem);
  const refundAmount = getRefundAmount(state);
  const conversationId = extractSelectedTicketIdFromState(state);
  const actionReasons = getActionReasons(state);

  const payload = {
    amount: refundAmount,
    orderId: orderId && orderId.toString(),
    comment:
      actionReasons?.refundPrimaryReason ||
      actionReasons?.cancelOrRefundPrimaryReason ||
      'This refund was for a price match discount',
    addNotes: true,
    shopName,
    conversationId,
  };

  await shopifyRefundAmount(payload);
  // sets a false value that will force the confirmation page
  // newly fetched orders will have this data populated
  dispatch(setRefundsOnShopifyOrder([{}]));
  trackDecisionTreeItemPartiallyRefunded();
};
